// The price book as a spreadsheet (Settings › Price book): the CSV the office downloads, edits in
// Excel or Sheets and brings back. Columns are found by their header names, in any order; only the
// code is required, and a column left out leaves that field as it is. The server checks every value
// (pricebook.previewImport): these only read and write the CSV.

import { refusal } from '../history/customer-edit-model.ts';

export const PB_COLUMNS = [
  { key: 'code', header: 'Code', names: ['code', 'item code', 'sku'] },
  { key: 'name', header: 'Name', names: ['name', 'item', 'item name'] },
  { key: 'type', header: 'Type', names: ['type', 'item type'] },
  { key: 'category', header: 'Category', names: ['category'] },
  { key: 'unit', header: 'Unit', names: ['unit', 'uom'] },
  { key: 'price', header: 'Price', names: ['price', 'customer price'] },
  { key: 'memberPrice', header: 'Member price', names: ['member price', 'members price', 'member'] },
  { key: 'cost', header: 'Cost', names: ['cost', 'our cost'] },
  { key: 'taxable', header: 'Taxable', names: ['taxable', 'tax'] },
  { key: 'active', header: 'Active', names: ['active', 'in use'] },
  { key: 'description', header: 'Description', names: ['description', 'details'] },
] as const;
export type PbKey = (typeof PB_COLUMNS)[number]['key'];
export type ImportRow = { line: number } & Partial<Record<PbKey, string>> & { code: string };

/** A CSV's rows (RFC 4180: quoted cells may hold commas, quotes and line breaks), each with the file line it starts on. */
export function parseCsv(text: string): { line: number; cells: string[] }[] {
  const rows: { line: number; cells: string[] }[] = [];
  const t = text.replace(/^\uFEFF/, '').replace(/\r\n?/g, '\n');
  let cells: string[] = [];
  let cur = '';
  let quoted = false;
  let line = 1;
  let start = 1;
  const end = () => {
    cells.push(cur);
    if (cells.some((c) => c.trim())) rows.push({ line: start, cells: cells.map((c) => c.trim()) });
    cells = [];
    cur = '';
  };
  for (let i = 0; i < t.length; i++) {
    const ch = t[i]!;
    if (quoted) {
      if (ch === '"' && t[i + 1] === '"') {
        cur += '"';
        i++;
      } else if (ch === '"') quoted = false;
      else {
        if (ch === '\n') line++;
        cur += ch;
      }
    } else if (ch === '"') quoted = true;
    else if (ch === ',') {
      cells.push(cur);
      cur = '';
    } else if (ch === '\n') {
      end();
      line++;
      start = line;
    } else cur += ch;
  }
  if (cur || cells.length) end();
  return rows;
}

/** Which column each field is in, from the header row; the headers it didn't know, and what's missing. */
export function mapHeader(header: readonly string[]): {
  at: Partial<Record<PbKey, number>>;
  unknown: string[];
  missing: string[];
} {
  const at: Partial<Record<PbKey, number>> = {};
  const unknown: string[] = [];
  header.forEach((h, i) => {
    const n = h.trim().toLowerCase().replace(/\s+/g, ' ');
    const col = PB_COLUMNS.find((c) => (c.names as readonly string[]).includes(n));
    if (col && at[col.key] === undefined) at[col.key] = i;
    else if (n) unknown.push(h.trim());
  });
  return { at, unknown, missing: at.code === undefined ? ['Code'] : [] };
}

/** A cell the download guarded against formulas ('=…) comes back as it was. */
const unguard = (s: string) => (/^'[=+\-@\t\r]/.test(s) ? s.slice(1) : s);

/** The rows to send: the cells under the header's columns; a column the file didn't have is left out. */
export function importRows(text: string): {
  rows: ImportRow[];
  fileRows: number;
  unknown: string[];
  error?: string;
} {
  const all = parseCsv(text);
  if (!all.length) return { rows: [], fileRows: 0, unknown: [], error: 'The file is empty.' };
  const { at, unknown, missing } = mapHeader(all[0]!.cells);
  if (missing.length)
    return {
      rows: [],
      fileRows: all.length - 1,
      unknown,
      error: `The first row needs a ${missing.join(' and a ')} column header, as the download has.`,
    };
  const rows = all.slice(1).map((r) => {
    const out: ImportRow = { line: r.line, code: '' };
    for (const c of PB_COLUMNS) {
      const i = at[c.key];
      if (i !== undefined) out[c.key] = unguard(r.cells[i] ?? '');
    }
    return out;
  });
  return { rows, fileRows: all.length - 1, unknown };
}

/**
 * The api takes a call of up to 1 MiB (1,048,576 bytes): rows with long descriptions pass it well
 * before 2,000 of them (a 2,000-row sheet with 400-character descriptions is about 1.3 MB). The whole
 * file is one call, so it's applied all or nothing; a file too big for one is split by the office.
 */
export const IMPORT_MAX_BYTES = 1_000_000;
const TOO_BIG = 'That file is too big to import in one go. Split it into smaller files and import each.';

/** A call the api refused as too large (413) says what to do, never a bare "Couldn’t save." */
export const importRefusal = (e: unknown) =>
  (e as { status?: number } | null)?.status === 413 ? TOO_BIG : refusal(e);

/** Why these rows can't go in one call, in words, with about how many would, or null when they fit. */
export function tooBig(send: { rows: ImportRow[]; fileRows: number }): string | null {
  const bytes = new TextEncoder().encode(JSON.stringify(send)).length;
  if (bytes <= IMPORT_MAX_BYTES) return null;
  const fit = Math.max(100, Math.floor((send.rows.length * IMPORT_MAX_BYTES) / bytes / 100) * 100);
  return `That file is too big to import in one go. Split it into files of about ${fit.toLocaleString('en-CA')} rows and import each.`;
}

export interface ExportItem {
  code: string;
  name: string;
  type: string;
  categoryName: string | null;
  unit: string;
  priceCents: number;
  memberPriceCents: number | null;
  costCents?: number | null;
  taxable: boolean;
  active: boolean;
  description: string | null;
}

const dollars = (c: number | null | undefined) => (c === null || c === undefined ? '' : (c / 100).toFixed(2));
/** One cell: quoted when it needs to be; text that a spreadsheet would run as a formula starts with '. */
function cell(v: string): string {
  const s = /^[=+\-@\t\r]/.test(v) ? `'${v}` : v;
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/** The download: every item, the Cost column only for those who see cost. */
export function exportCsv(items: readonly ExportItem[], withCost: boolean): string {
  const cols = PB_COLUMNS.filter((c) => withCost || c.key !== 'cost');
  const value = (i: ExportItem, k: PbKey): string =>
    k === 'code'
      ? i.code
      : k === 'name'
        ? i.name
        : k === 'type'
          ? i.type
          : k === 'category'
            ? (i.categoryName ?? '')
            : k === 'unit'
              ? i.unit
              : k === 'price'
                ? dollars(i.priceCents)
                : k === 'memberPrice'
                  ? dollars(i.memberPriceCents)
                  : k === 'cost'
                    ? dollars(i.costCents)
                    : k === 'taxable'
                      ? i.taxable
                        ? 'yes'
                        : 'no'
                      : k === 'active'
                        ? i.active
                          ? 'yes'
                          : 'no'
                        : (i.description ?? '');
  return [
    cols.map((c) => c.header).join(','),
    ...items.map((i) => cols.map((c) => cell(value(i, c.key))).join(',')),
  ].join('\r\n');
}

/** A preview's change in words: money from cents ("$189.00"), the rest as it is; nothing is "(none)". */
export function changeWords(field: string, v: string | null): string {
  if (v === null || v === '') return '(none)';
  if (field === 'price' || field === 'memberPrice' || field === 'cost')
    return new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(Number(v) / 100);
  return v;
}
export const FIELD_WORDS: Record<string, string> = {
  name: 'Name',
  type: 'Type',
  category: 'Category',
  unit: 'Unit',
  price: 'Price',
  memberPrice: 'Member price',
  cost: 'Cost',
  taxable: 'Taxable',
  active: 'Active',
  description: 'Description',
};
